import { forwardRef, useEffect, useImperativeHandle, useRef, type KeyboardEvent, type ReactNode } from "react";
import { EditorContent, useEditor, useEditorState, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "@tiptap/markdown";
import { Placeholder } from "@tiptap/extensions";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { TableKit } from "@tiptap/extension-table";
import { Bold, Heading2, Heading3, Italic, Link2, Link2Off, List, ListChecks, ListOrdered, Redo2, Strikethrough, Table2, TextQuote, Undo2 } from "lucide-react";
import { cn } from "../lib/cn";
import { useFieldLabel } from "./form";
import { useMicrocopy, type MicrocopyKey } from "../lib/microcopy";
import { useFieldDraft } from "../lib/draft";
import { DraftRestoredNote } from "./draft-note";

/** Imperative handle for callers that need to steer the editor (focus it, replace its text after an AI redraft, insert a snippet). */
export interface RichTextEditorHandle {
  focus: (position?: "start" | "end") => void;
  /** Replaces the whole document. Emits `onChange` unless `silent` is true. */
  setMarkdown: (markdown: string, silent?: boolean) => void;
  /** Inserts markdown at the caret (or appends when the editor is empty). */
  insertMarkdown: (markdown: string) => void;
  getMarkdown: () => string;
  /** The DOM node of the editable area, for scrolling it into view. */
  element: () => HTMLElement | null;
}

export interface RichTextEditorProps {
  /** The document as GFM markdown — what the platform stores and renders everywhere else. */
  value: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  /** `compact` hides the block-level tools (headings, quote, table) for short texts such as a finding response. */
  toolbar?: "full" | "compact" | "none";
  /** Extra controls rendered at the end of the toolbar — the AI redraft button, a word count, anything the surface needs. */
  actions?: ReactNode;
  /** Fired on ⌘/Ctrl+Enter, the platform's "record" hotkey. */
  onSubmit?: () => void;
  /** Explicit text direction; `auto` lets the browser pick per document (Arabic drafts read right-to-left). */
  dir?: "auto" | "ltr" | "rtl";
  minHeightClassName?: string;
  /** Classes for the scrolling content area, e.g. `max-h-96 overflow-y-auto` for long drafts inside a dialog. */
  contentClassName?: string;
  className?: string;
  id?: string;
  "aria-labelledby"?: string;
  "aria-label"?: string;
  "aria-invalid"?: boolean;
  autoFocus?: boolean;
  /** Keeps what the officer writes here on this device under this name until the server has it, restored first on reopening (S 2.7; see `Textarea`). */
  draft?: string | null;
}

/**
 * The platform's rich-text editor: officers write and correct AI drafts as formatted text, the document is stored as markdown so
 * every reader (case file, PDF exports, the AI itself) sees the same thing. Built on TipTap; the markdown round-trip is lossless for
 * the subset the drafts use (headings, emphasis, lists, checklists, quotes, links, tables).
 */
export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(function RichTextEditor(
  { value, onChange, placeholder, readOnly = false, disabled = false, toolbar = "full", actions, onSubmit, dir = "auto", minHeightClassName = "min-h-32", contentClassName, className, id, autoFocus = false, "aria-labelledby": labelledBy, "aria-label": ariaLabel, "aria-invalid": invalid, draft },
  ref,
) {
  const mc = useMicrocopy();
  const field = useFieldLabel();
  // Inside a Field, the editor is named by the field's label (a label's `for` does not reach a contenteditable element).
  const namedBy = labelledBy ?? (!ariaLabel && field && field.id === id ? field.labelId : undefined);
  const editable = !readOnly && !disabled;
  // The last markdown this editor emitted, so a parent echoing the value back does not reset the caret mid-typing.
  const lastEmitted = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  // A restored draft reaches the screen's state through onChange and comes back as `value`, which replaces the document below. Only the
  // officer's own edits (the editor's updates) are kept, not a document the screen sets.
  const edited = useRef(false);
  const kept = useFieldDraft(draft, value, (v) => onChangeRef.current(v), { enabled: editable, edited });

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        link: { openOnClick: false, autolink: true, defaultProtocol: "https", HTMLAttributes: { rel: "noopener noreferrer nofollow" } },
        // Underline has no markdown form and would be lost on save.
        underline: false,
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
      TableKit.configure({ table: { resizable: false } }),
      Placeholder.configure({ placeholder: placeholder ?? mc("editorPlaceholder"), showOnlyWhenEditable: true }),
      Markdown,
    ],
    content: value,
    contentType: "markdown",
    editable,
    autofocus: autoFocus ? "end" : false,
    // Re-renders are driven by `useEditorState` selectors below; a re-render per keystroke is not needed.
    shouldRerenderOnTransaction: false,
    editorProps: {
      attributes: { class: "ncp-editor-content focus:outline-none", dir, ...(id ? { id } : {}), ...(namedBy ? { "aria-labelledby": namedBy } : {}), ...(ariaLabel ? { "aria-label": ariaLabel } : {}), ...(invalid ? { "aria-invalid": "true" } : {}), role: "textbox", "aria-multiline": "true" },
      handleKeyDown: (_view, event) => {
        if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && onSubmitRef.current) {
          event.preventDefault();
          onSubmitRef.current();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: e }) => {
      const md = e.getMarkdown();
      lastEmitted.current = md;
      edited.current = true;
      onChangeRef.current(md);
    },
  });

  // External value changes (another criterion selected, "start from the platform's text", an AI redraft applied) replace the
  // document; the editor's own edits echo back through `value` and are ignored here.
  useEffect(() => {
    if (!editor || value === lastEmitted.current) return;
    lastEmitted.current = value;
    editor.commands.setContent(value, { contentType: "markdown", emitUpdate: false });
  }, [editor, value]);

  useEffect(() => { editor?.setEditable(editable); }, [editor, editable]);

  useImperativeHandle(ref, () => ({
    focus: (position = "end") => editor?.chain().focus(position, { scrollIntoView: false }).run(),
    setMarkdown: (markdown, silent = false) => {
      if (!editor) return;
      lastEmitted.current = markdown;
      editor.commands.setContent(markdown, { contentType: "markdown", emitUpdate: false });
      if (!silent) onChangeRef.current(markdown);
    },
    insertMarkdown: (markdown) => {
      if (!editor) return;
      editor.chain().focus("end", { scrollIntoView: false }).insertContent(markdown, { contentType: "markdown" }).run();
    },
    getMarkdown: () => editor?.getMarkdown() ?? "",
    element: () => editor?.view.dom ?? null,
  }), [editor]);

  return (
    <>
      {kept.restored ? <DraftRestoredNote onDiscard={kept.discard} /> : null}
      <div
        className={cn(
          "ncp-editor group/editor flex flex-col rounded-lg border border-input bg-white/80 text-sm text-foreground shadow-[inset_0_1px_2px_rgb(43_38_32/0.04)] transition-colors focus-within:border-aegold-400 focus-within:ring-2 focus-within:ring-ring",
          invalid && "border-danger-300 focus-within:border-danger-400 focus-within:ring-danger-500",
          disabled && "cursor-not-allowed opacity-60",
          className,
        )}
      >
        {toolbar !== "none" && editable ? <Toolbar editor={editor} compact={toolbar === "compact"} actions={actions} mc={mc} /> : actions && editable ? <div className="flex items-center justify-end gap-1 border-b border-sand-200 px-1.5 py-1">{actions}</div> : null}
        <EditorContent editor={editor} className={cn("flex-1 px-3 py-2 scrollbar-thin", minHeightClassName, contentClassName, "[&_.ncp-editor-content]:h-full [&_.ncp-editor-content]:min-h-[inherit]")} />
      </div>
    </>
  );
});

// ---------------------------------------------------------------- Toolbar
/** `mirror`: an icon with a direction (a list's bullets, undo's arrow) that turns round on a right-to-left screen. */
type Tool = { key: MicrocopyKey; icon: typeof Bold; active?: boolean; disabled?: boolean; mirror?: boolean; run: () => void };

function Toolbar({ editor, compact, actions, mc }: { editor: Editor; compact: boolean; actions?: ReactNode; mc: (key: MicrocopyKey) => string }) {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"), italic: e.isActive("italic"), strike: e.isActive("strike"),
      h2: e.isActive("heading", { level: 2 }), h3: e.isActive("heading", { level: 3 }),
      bullet: e.isActive("bulletList"), ordered: e.isActive("orderedList"), task: e.isActive("taskList"),
      quote: e.isActive("blockquote"), link: e.isActive("link"), table: e.isActive("table"),
      canUndo: e.can().undo(), canRedo: e.can().redo(),
    }),
  });

  const setLink = () => {
    const previous = (editor.getAttributes("link").href as string | undefined) ?? "";
    // A native prompt keeps the editor dependency-free of dialogs; the address is validated by the Link extension.
    const href = window.prompt(mc("editorLinkPrompt"), previous);
    if (href === null) return;
    if (href.trim() === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: href.trim() }).run();
  };

  const inline: Tool[] = [
    { key: "editorBold", icon: Bold, active: state.bold, run: () => editor.chain().focus().toggleBold().run() },
    { key: "editorItalic", icon: Italic, active: state.italic, run: () => editor.chain().focus().toggleItalic().run() },
    { key: "editorStrike", icon: Strikethrough, active: state.strike, run: () => editor.chain().focus().toggleStrike().run() },
  ];
  const blocks: Tool[] = compact ? [] : [
    { key: "editorHeading", icon: Heading2, active: state.h2, run: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
    { key: "editorSubheading", icon: Heading3, active: state.h3, run: () => editor.chain().focus().toggleHeading({ level: 3 }).run() },
  ];
  const lists: Tool[] = [
    { key: "editorBulletList", icon: List, mirror: true, active: state.bullet, run: () => editor.chain().focus().toggleBulletList().run() },
    { key: "editorOrderedList", icon: ListOrdered, active: state.ordered, run: () => editor.chain().focus().toggleOrderedList().run() },
    { key: "editorTaskList", icon: ListChecks, active: state.task, run: () => editor.chain().focus().toggleTaskList().run() },
  ];
  const extras: Tool[] = [
    ...(compact ? [] : [{ key: "editorQuote" as MicrocopyKey, icon: TextQuote, mirror: true, active: state.quote, run: () => editor.chain().focus().toggleBlockquote().run() }]),
    state.link
      ? { key: "editorUnlink", icon: Link2Off, active: true, run: () => editor.chain().focus().extendMarkRange("link").unsetLink().run() }
      : { key: "editorLink", icon: Link2, run: setLink },
    ...(compact ? [] : [{ key: "editorTable" as MicrocopyKey, icon: Table2, active: state.table, disabled: state.table, run: () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() }]),
  ];
  const history: Tool[] = [
    { key: "editorUndo", icon: Undo2, mirror: true, disabled: !state.canUndo, run: () => editor.chain().focus().undo().run() },
    { key: "editorRedo", icon: Redo2, mirror: true, disabled: !state.canRedo, run: () => editor.chain().focus().redo().run() },
  ];

  const groups = [inline, blocks, lists, extras, history].filter((g) => g.length > 0);
  return (
    <div role="toolbar" aria-label={mc("editorFormatting")} className="flex flex-wrap items-center gap-0.5 border-b border-sand-200 px-1.5 py-1" onMouseDown={(e) => e.preventDefault() /* keep the selection in the editor */}>
      {groups.map((group, gi) => (
        <div key={gi} className="flex items-center gap-0.5 after:mx-1 after:h-4 after:w-px after:bg-sand-200 last:after:hidden">
          {group.map((tool) => <ToolButton key={tool.key} tool={tool} label={mc(tool.key)} />)}
        </div>
      ))}
      {actions ? <div className="ms-auto flex items-center gap-1" onMouseDown={(e) => e.stopPropagation()}>{actions}</div> : null}
    </div>
  );
}

function ToolButton({ tool, label }: { tool: Tool; label: string }) {
  const Icon = tool.icon;
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); tool.run(); } };
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={tool.active ?? undefined}
      disabled={tool.disabled}
      onClick={tool.run}
      onKeyDown={onKeyDown}
      className={cn(
        "grid size-7 place-items-center rounded-md text-sand-700 transition-colors hover:bg-sand-100 hover:text-aeblack-900 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent",
        tool.active && "bg-aegold-100 text-aegold-900 hover:bg-aegold-100",
      )}
    >
      <Icon className={cn("size-3.5", tool.mirror && "rtl:-scale-x-100")} />
    </button>
  );
}
